// ═══ 업무별 기록 v5 — 확인 항목 (엑셀과 장부가 이어지지 않는 곳, DESIGN_V5 §1.2·§4·§6.5) ═══
// 항목마다 '장부대로 둠' / '엑셀대로 정리'(G4 는 '장부에 반영') 중 하나를 고른다. 정리 줄은 2026년 8월 장부에 들어간다.
// carry 항목(G3·G5·G6)은 묶음 반영 때 함께 정하고, 반영된 동안은 [바꾸기]만 된다(단독 취소 불가).
// 결정 뒤 장부가 바뀌면 '차이'를 보여 주고 [다시 맞춤]으로 맞춘다.
// 서버 계약:
//   GET  /work-records/gaps?jobKind= → {success, gaps:[{gapId, no, kind, sheet, label, keepLabel, excelLabel, delta, sourceCard, rows, decision, drift, canDecide, blockedBy}]}
//   GET  /work-records/gaps/:gapId/preview?choice= → {success, preview:{lines, detached, run, byUnitHash}}
//   POST /work-records/gaps/:gapId/decide {choice, deciderName, memo, acceptDetached, replace, expect:{byUnitHash}} → {success, decisionId, lines, runId}
//        409 SOURCE_DETACHED {rows, deltaByUnit:[{unitId, delta}]} ('excel' 일 때만) → 미리보기 재조회 + 확인 후 acceptDetached:true
//        409 PREVIEW_CHANGED {preview:{lines(unitName 없음), byUnitHash}} → GET preview 재조회
//        409 SAME_CHOICE (같은 선택을 다시 보냈는데 그대로 둘 수 없음) · ERP_OVERLAP{rows} (G4 반영 — 겹친 건을 먼저 정한다)
//   G4 'post' 미리보기 run.overlap = ERP 에 이미 있는 건 → [ERP 에 이미 있는 건 정하기] = RecordsOverlapModal(gap) → POST gaps/:id/overlap → 미리보기 재조회
//   GET  /work-records/gaps/:gapId/realign-preview → {success, preview:{lines:[{unitId, unitName, amount}], byUnitHash}} (읽기 전용, lines [] = 차이 없음)
//   POST /work-records/gaps/:gapId/realign {memo, expect:{byUnitHash}} → {success, lines} · 409 NO_DRIFT · NOT_DECIDED · PREVIEW_CHANGED → 재조회
//   POST /work-records/gaps/:gapId/undo {memo} → {success} · 409 CARRY_GAP_LOCKED · NOT_DECIDED
//
// props: jobKind · reloadKey · userName · showToast · onChanged
import React, { useState, useEffect } from 'react';
import { is } from './SettlementItemDrawer';
import { formatArrivedAtKst } from '../utils/dateKst';
import { fmt, num, signed, signColor, getJson, postJson, codeMessage, overlay, box, noteBox, errBoxSt, CHOICE_LABEL,
  PostUnitTable, LinesTable, DetachedList } from './RecordsPreviewModal';
import RecordsOverlapModal from './RecordsOverlapModal';

var BLOCK_TEXT = {
  CARD_NOT_POSTED: function (g) { return (g.sourceCard ? g.sourceCard.no : '이 묶음') + ' 카드를 먼저 장부에 반영해 주세요.'; },
  DECIDE_WITH_POST: function (g) { return (g.sourceCard ? g.sourceCard.no : '이 묶음') + ' 카드를 장부에 반영할 때 함께 정합니다.'; },
  UNLINKED_REMAIN: function () { return '아래 기록을 먼저 업체에 연결해 주세요.'; }
};
var inputSt = Object.assign({}, is, { marginTop: 4, fontSize: 14 });
var labelSt = { display: 'block', marginTop: 12, fontSize: 13.5, fontWeight: 700, color: '#475569' };
var bigBtn = { flex: 1, justifyContent: 'center', minHeight: 44, fontSize: 15 };
var choicesOf = function (g) {
  return [['keep', g.keepLabel || CHOICE_LABEL.keep], g.kind === 'extra' ? ['post', g.excelLabel || CHOICE_LABEL.post] : ['excel', g.excelLabel || CHOICE_LABEL.excel]];
};

// 결정 / 바꾸기 / 다시 맞춤 / 결정 취소 창 — action: 'decide' | 'replace' | 'realign' | 'undo'
function GapModal(props) {
  var g = props.gap, action = props.action;
  var showToast = props.showToast;
  var isDecide = action === 'decide' || action === 'replace';
  var cur = g.decision;
  var [choice, setChoice] = useState(isDecide ? '' : (cur ? cur.choice : ''));
  var [pv, setPv] = useState(null);
  var [pvErr, setPvErr] = useState('');
  var [reloadN, setReloadN] = useState(0);
  var [decider, setDecider] = useState(props.userName || '');
  var [memo, setMemo] = useState('');
  var [detached, setDetached] = useState(null);   // 409 SOURCE_DETACHED 응답 {rows, deltaByUnit}
  var [accept, setAccept] = useState(false);
  var [notice, setNotice] = useState('');
  var [busy, setBusy] = useState(false);
  var [ovOpen, setOvOpen] = useState(false);
  var needPreview = (isDecide && !!choice) || action === 'realign';
  useEffect(function () {
    if (!needPreview) { setPv(null); return undefined; }
    var alive = true;
    setPv(null); setPvErr('');
    getJson('/work-records/gaps/' + g.gapId + (action === 'realign' ? '/realign-preview' : '/preview?choice=' + choice)).then(function (res) {
      if (!alive) return;
      var d = res.data;
      if (d.success && d.preview) setPv(d.preview);
      else setPvErr(d.code ? codeMessage(d.code, d) : (d.error || '미리보기를 만들지 못했습니다'));
    }).catch(function () { if (alive) setPvErr('서버 오류'); });
    return function () { alive = false; };
  }, [g.gapId, action, choice, needPreview, reloadN]);

  var p = pv || {};
  var detachedRows = (detached && detached.rows) || (isDecide ? p.detached : null) || [];
  var needAccept = isDecide && choice === 'excel' && detachedRows.length > 0;
  var memoOk = memo.trim().length > 0 && memo.trim().length <= 200;
  var noDrift = action === 'realign' && !!pv && !(p.lines || []).length;
  var ovRows = choice === 'post' && p.run ? p.run.overlap || [] : [];
  var ovPending = ovRows.filter(function (o) { return !o.choice; }).length;
  var canRun = !busy && memoOk && !noDrift && !ovPending && (action === 'undo' || !!pv) && (!isDecide || (!!choice && decider.trim().length > 0)) && (!needAccept || accept);

  var run = function () {
    if (!canRun) return;
    var url = '/work-records/gaps/' + g.gapId + '/' + (isDecide ? 'decide' : action);
    var body = { memo: memo.trim() };
    if (isDecide) body = { choice: choice, deciderName: decider.trim(), memo: memo.trim(), acceptDetached: accept, replace: action === 'replace', expect: { byUnitHash: p.byUnitHash } };
    if (action === 'realign') body.expect = { byUnitHash: p.byUnitHash };
    setBusy(true); setNotice('');
    postJson(url, body).then(function (res) {
      setBusy(false);
      var d = res.data;
      if (d.success) {
        showToast(g.no + (action === 'undo' ? ' 결정을 취소했습니다' : action === 'realign' ? ' 장부를 다시 맞췄습니다' : ' 결정했습니다 — ' + CHOICE_LABEL[choice]));
        props.onDone(); return;
      }
      if (d.code === 'SOURCE_DETACHED') { setDetached({ rows: d.rows || [] }); setAccept(false); setReloadN(function (n) { return n + 1; }); return; }
      if (d.code === 'PREVIEW_CHANGED') { setNotice(codeMessage(d.code, d)); setReloadN(function (n) { return n + 1; }); return; }
      if (d.code === 'ERP_OVERLAP') { setNotice('ERP 장부에 이미 있는 차량이 있습니다. [ERP 에 이미 있는 건 정하기]로 먼저 정해 주세요.'); setReloadN(function (n) { return n + 1; }); return; }
      if (d.code === 'ALREADY_DECIDED' || d.code === 'NOT_DECIDED' || d.code === 'NO_DRIFT') { showToast(codeMessage(d.code, d), 'err'); props.onDone(); return; }
      setNotice(codeMessage(d.code, d));
    }).catch(function () { setBusy(false); setNotice('서버 오류 — 잠시 뒤 다시 눌러 주세요.'); });
  };

  var title = g.no + (action === 'undo' ? ' 결정 취소' : action === 'realign' ? ' 다시 맞춤' : action === 'replace' ? ' 결정 바꾸기' : ' 결정하기');
  return (<div style={overlay} onClick={function () { if (!busy) props.onClose(); }}>
    <div role="dialog" aria-label={title} onClick={function (e) { e.stopPropagation(); }} style={box}>
      <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#1B3A5C' }}>{title}</h3>
      <p style={{ margin: '0 0 12px', fontSize: 14, color: '#475569', lineHeight: 1.6 }}>{g.label}</p>

      {isDecide && <div role="radiogroup" aria-label="결정" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {choicesOf(g).map(function (c) {
          var on = choice === c[0];
          return (<button key={c[0]} type="button" role="radio" aria-checked={on} aria-label={c[1]} onClick={function () { setChoice(c[0]); setDetached(null); setAccept(false); }}
            style={{ flex: 1, minWidth: 200, textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 800,
              border: '2px solid ' + (on ? '#2563EB' : '#e2e8f0'), background: on ? '#eff6ff' : '#fff', color: on ? '#1d4ed8' : '#334155' }}>{on ? '● ' : '○ '}{c[1]}</button>);
        })}
      </div>}
      {action === 'realign' && <div style={{ fontSize: 14, color: '#475569' }}>결정한 뒤 장부가 바뀌어 생긴 차이 <b style={{ color: signColor(g.drift && g.drift.amount) }}>{signed(g.drift && g.drift.amount)}원</b> 만큼 2026년 8월 장부에 정리 줄을 더 넣습니다.
        {!!pv && <div style={{ marginTop: 8 }}><LinesTable lines={p.lines} amountLabel="더 넣을 정리 금액" empty="지금은 다시 맞출 차이가 없습니다." /></div>}</div>}
      {action === 'undo' && <div style={{ fontSize: 14, color: '#475569' }}>이 결정으로 넣은 정리 줄{cur && cur.runId ? '과 반영한 기록' : ''}을 장부에서 되돌립니다.
        {!!cur && <div style={{ marginTop: 8 }}><LinesTable lines={cur.lines} amountLabel="되돌릴 정리 금액" empty="되돌릴 정리 줄이 없습니다." /></div>}</div>}

      {!!pvErr && <div style={errBoxSt}>{pvErr}</div>}
      {needPreview && !pv && !pvErr && <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>미리보기 만드는 중…</div>}
      {!!pv && isDecide && <div style={{ marginTop: 12 }}>
        {p.run ? <PostUnitTable rows={p.run.byUnit} totals={p.run.totals} /> : <LinesTable lines={p.lines} empty={choice === 'keep' ? '장부는 그대로 두고 결정만 남깁니다.' : undefined} />}
      </div>}
      {ovRows.length > 0 && <div style={noteBox} data-testid="gap-overlap">
        <b>ERP 장부에 이미 있는 차량 {fmt(ovRows.length)}건</b>{ovPending ? ' — 아직 정하지 않은 ' + fmt(ovPending) + '건이 있어 반영할 수 없습니다.' : ' — 모두 정했습니다.'}
        {ovRows.map(function (o) {
          return <div key={o.recordId}>{o.srcRow}행 · {o.plate || '-'} · ERP {o.erpMonth} {o.erpJobKind} {fmt(o.erpSubtotal)}원 · {o.choice === 'exclude' ? '엑셀 건 빼기' : o.choice === 'separate' ? '별건으로 넣기' : '정하지 않음'}</div>;
        })}
        <div style={{ marginTop: 6 }}><button className="btn btn-blue btn-sm" onClick={function () { setOvOpen(true); }}>ERP 에 이미 있는 건 정하기</button></div>
      </div>}
      {needAccept && <div data-testid="gap-detached">
        <DetachedList rows={detachedRows} title="장부에서 직접 고친 기록이 있습니다" note="고친 내용을 기준으로 정리 금액을 계산합니다." />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 14, fontWeight: 700, color: '#92400e' }}>
          <input type="checkbox" aria-label="고친 내용 기준으로 정리" checked={accept} onChange={function (e) { setAccept(e.target.checked); }} style={{ width: 20, height: 20 }} />
          고친 내용 기준으로 정리합니다
        </label>
      </div>}

      {isDecide && <label style={labelSt}>확인한 사람 이름
        <input aria-label="확인한 사람 이름" value={decider} maxLength={50} onChange={function (e) { setDecider(e.target.value); }} style={inputSt} /></label>}
      <label style={labelSt}>{action === 'undo' ? '취소 이유' : '메모'} (꼭 적어 주세요)
        <input aria-label={action === 'undo' ? '취소 이유' : '메모'} value={memo} maxLength={200} onChange={function (e) { setMemo(e.target.value); }} style={inputSt} /></label>
      {!!notice && <div style={noteBox} data-testid="gap-notice">{notice}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button className="btn btn-outline" style={bigBtn} disabled={busy} onClick={props.onClose}>닫기</button>
        <button className="btn btn-blue" style={Object.assign({}, bigBtn, canRun ? null : { opacity: 0.45, cursor: 'not-allowed' })} disabled={!canRun} onClick={run}>
          {busy ? '처리 중…' : action === 'undo' ? '결정 취소' : action === 'realign' ? '다시 맞춤' : '결정'}</button>
      </div>
      {ovOpen && <RecordsOverlapModal gap={g} showToast={showToast}
        onClose={function () { setOvOpen(false); setNotice(''); setReloadN(function (n) { return n + 1; }); }} />}
    </div>
  </div>);
}

function GapCard(props) {
  var g = props.gap, d = g.decision, drift = g.drift || {};
  var carry = g.kind === 'carry';
  var posted = !!(g.sourceCard && g.sourceCard.posted);
  var rows = g.rows || [];
  var act = function (a) { return function () { props.onAction(a, g); }; };
  return (<div data-testid="gap-card" aria-label={'확인 항목 ' + g.no} style={{ border: '1px solid ' + (d ? '#bbf7d0' : '#fde68a'), background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 17, fontWeight: 900, color: '#1B3A5C' }}>{g.no}</span>
      <span style={{ fontSize: 15, fontWeight: 700, color: '#334155' }}>{g.label}</span>
      <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 800, color: signColor(g.delta) }}>엑셀과 차이 {signed(g.delta)}원</span>
    </div>
    <div style={{ marginTop: 4, fontSize: 13, color: '#94a3b8' }}>{g.sheet} · {g.sourceCard ? g.sourceCard.no + ' 카드 ' + (posted ? '반영됨' : '반영 전') : ''}</div>
    {rows.length > 0 && <div style={{ marginTop: 8, fontSize: 13.5, color: '#475569' }}>{rows.map(function (r) {
      return <div key={r.recordId}>{r.srcRow}행 · {r.plate || '-'} · {fmt(r.amount)}원 · {r.unitName || <span style={{ color: '#b45309' }}>업체 미연결</span>}</div>;
    })}</div>}

    {!!d && <div data-testid="gap-decision" style={{ marginTop: 10, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13.5, color: '#166534', lineHeight: 1.6 }}>
      <b>결정: {CHOICE_LABEL[d.choice] || d.choice}</b> · 확인 {d.deciderName || '-'} · {d.createdByName || '-'}{d.createdByStaff ? ' (직원)' : ''} · {formatArrivedAtKst(d.createdAt) || '-'}
      {!!d.memo && <div>메모: {d.memo}</div>}
      {(d.lines || []).length > 0 && <div>정리 줄: {d.lines.map(function (l) { return (l.unitName || l.unitId) + ' ' + signed(l.amount) + (l.kind === 'realign' ? '(다시 맞춤)' : ''); }).join(', ')}</div>}
    </div>}
    {!!d && num(drift.amount) !== 0 && <div data-testid="gap-drift" style={{ marginTop: 10, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13.5, color: '#92400e' }}>
      결정한 뒤 장부가 바뀌어 <b>{signed(drift.amount)}원</b> 차이가 있습니다{(drift.byUnit || []).length ? ' (' + drift.byUnit.map(function (u) { return (u.unitName || u.unitId) + ' ' + signed(u.amount); }).join(', ') + ')' : ''}.
      <div style={{ marginTop: 6 }}><button className="btn btn-blue btn-sm" onClick={act('realign')}>다시 맞춤</button></div>
    </div>}

    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
      {!d && g.canDecide && <button className="btn btn-blue" style={{ minHeight: 44, fontSize: 15, padding: '0 22px' }} onClick={act('decide')}>결정하기</button>}
      {!d && !g.canDecide && <span style={{ fontSize: 13.5, color: '#b45309' }}>{(BLOCK_TEXT[g.blockedBy] || function () { return '지금은 정할 수 없습니다.'; })(g)}</span>}
      {!!d && <button className="btn btn-outline" style={{ minHeight: 40, fontSize: 14 }} onClick={act('replace')}>결정 바꾸기</button>}
      {!!d && !carry && <button className="btn btn-outline" style={{ minHeight: 40, fontSize: 14 }} onClick={act('undo')}>결정 취소</button>}
      {!!d && carry && <span style={{ fontSize: 13, color: '#64748b' }}>{g.sourceCard ? g.sourceCard.no : ''} 반영을 취소하면 이 결정도 함께 취소됩니다.</span>}
    </div>
  </div>);
}

export default function RecordsGaps(props) {
  var jobKind = props.jobKind;
  var showToast = props.showToast || function () {};
  var onChanged = props.onChanged || function () {};
  var [gaps, setGaps] = useState(null);
  var [err, setErr] = useState('');
  var [modal, setModal] = useState(null);   // {action, gap}

  useEffect(function () {
    var alive = true;
    setErr('');
    getJson('/work-records/gaps?jobKind=' + encodeURIComponent(jobKind)).then(function (res) {
      if (!alive) return;
      if (!res.data.success || !Array.isArray(res.data.gaps)) { setErr(res.data.error || '조회 실패'); setGaps([]); return; }
      setGaps(res.data.gaps);
    }).catch(function () { if (alive) { setErr('조회 실패'); setGaps([]); } });
    return function () { alive = false; };
  }, [jobKind, props.reloadKey]);

  return (<div>
    <p style={{ margin: '0 0 10px', fontSize: 14, color: '#475569', lineHeight: 1.7 }}>
      엑셀과 장부가 이어지지 않는 곳입니다. 항목마다 '장부대로 둠' 또는 '엑셀대로 정리'를 골라 주세요. 정리 금액은 2026년 8월 장부에 들어갑니다.
    </p>
    {!!err && <div style={errBoxSt}>{err}</div>}
    {gaps === null && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>조회 중…</div>}
    {!!gaps && !gaps.length && !err && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>확인할 항목이 없습니다.</div>}
    {(gaps || []).map(function (g) {
      return <GapCard key={g.gapId} gap={g} onAction={function (a, gap) { setModal({ action: a, gap: gap }); }} />;
    })}
    {!!modal && <GapModal action={modal.action} gap={modal.gap} userName={props.userName} showToast={showToast}
      onClose={function () { setModal(null); }} onDone={function () { setModal(null); onChanged(); }} />}
  </div>);
}
